import React from "react";
import {
  DatePicker,
  DateRange,
  Select,
  SelectOption,
} from "@veneer/core";
import moment from "moment";
import useSearchSelect from "../../../hooks/searchSelect/useSearchSelect";
import { MAX_AGENDA_RANGE_MONTHS } from "../agendaConstants";
import { truncatePosLabel } from "../../../utils/conversion";
import {
  WeekDayKey,
  DaySchedule,
  WEEKLY_AGENDA_MAX_RANGE_DAYS,
  MOMENT_DAY_MAP,
} from "../../../hooks/weeklyAgenda/useWeeklyAgenda";
import DayScheduleRow from "../../../components/AgendaCalendar/DayScheduleRow";

interface WeeklyAgendaCreationBlockProps {
  typeOfVisitID: number;

  labelSubretail: string;
  handleChangeSubRetail: (value: any) => void;
  handleChangeSubRetailList: (value: any) => void;
  subretailData: SelectOption[];
  subRetail: any[];
  onClearSubRetail: () => void;
  disableSubretail: boolean;
  subretailDataCopy: SelectOption[];
  setSubretailData: React.Dispatch<React.SetStateAction<SelectOption[]>>;

  labelPointOfSale: string;
  handleChangePointOfSale: (value: any) => void;
  handleChangePointOfSaleList: (value: any) => void;
  pointOfSaleData: SelectOption[];
  pointOfSale: any[];
  setPointOfSale: (value: any) => void;
  disablePointOfSale: boolean;
  errorPos: boolean;
  pointOfSaleDataCopy: SelectOption[];
  setPointOfSaleData: React.Dispatch<React.SetStateAction<SelectOption[]>>;

  labelStardDate: string;
  labelEndDate: string;
  startDate: string;
  endDate: string;
  handleChangeStartDate: (date: DateRange) => void;
  errorDatePicker: boolean;

  theme: any;
  labelStartTime: string;
  labelEndTime: string;

  visibleWeekdays: WeekDayKey[];
  daySchedules: Record<WeekDayKey, DaySchedule>;
  dayLabels: Record<WeekDayKey, string>;
  onDayToggle: (day: WeekDayKey) => void;
  onDayStartTime: (day: WeekDayKey, time: any) => void;
  onDayEndTime: (day: WeekDayKey, time: any) => void;
}

export const WeeklyAgendaCreationBlock: React.FC<WeeklyAgendaCreationBlockProps> = (props) => {
  const {
    typeOfVisitID,
    labelSubretail, handleChangeSubRetail, handleChangeSubRetailList,
    subretailData, subRetail, onClearSubRetail, disableSubretail,
    subretailDataCopy, setSubretailData,
    labelPointOfSale, handleChangePointOfSale, handleChangePointOfSaleList,
    pointOfSaleData, pointOfSale, setPointOfSale,
    disablePointOfSale, errorPos, pointOfSaleDataCopy, setPointOfSaleData,
    labelStardDate, labelEndDate, startDate, endDate,
    handleChangeStartDate, errorDatePicker,
    theme, labelStartTime, labelEndTime,
    visibleWeekdays, daySchedules, dayLabels,
    onDayToggle, onDayStartTime, onDayEndTime,
  } = props;

  const { handleSearch } = useSearchSelect();
  const weeklyIsOutsideRange = (day: any): boolean => {
    if (day.isBefore(moment(), "day")) return true;
    if (!startDate) {
      return day.isAfter(moment().add(MAX_AGENDA_RANGE_MONTHS, "months"), "day");
    }
    const start = moment(startDate);
    return day.isAfter(start.clone().add(WEEKLY_AGENDA_MAX_RANGE_DAYS, "days"), "day");
  };

  const renderSubRetailSelect = (multiple = false) => (
    <Select
      className="createAgendaSelect"
      label={labelSubretail}
      onChange={(e) =>
        multiple ? handleChangeSubRetailList(e.value) : handleChangeSubRetail(e.value)
      }
      options={subretailData}
      value={subRetail}
      onClear={() => onClearSubRetail()}
      disabled={disableSubretail}
      multiple={multiple || undefined}
      onSearch={(query: string) =>
        handleSearch(query, subretailDataCopy, setSubretailData)
      }
      searchDelay={500}
      showSearch={true}
    />
  );

  const renderPointOfSaleSelect = (multiple = false) => (
    <Select
      className="createAgendaSelect"
      label={labelPointOfSale}
      onChange={(e) =>
        multiple ? handleChangePointOfSaleList(e.value) : handleChangePointOfSale(e.value)
      }
      options={pointOfSaleData.map(truncatePosLabel)}
      value={pointOfSale}
      onClear={() => setPointOfSale([])}
      disabled={disablePointOfSale}
      error={errorPos}
      multiple={multiple || undefined}
      onSearch={(query: string) =>
        handleSearch(query, pointOfSaleDataCopy, setPointOfSaleData)
      }
      searchDelay={500}
      showSearch={true}
    />
  );

  const todayInRangeKey: WeekDayKey | null = (() => {
    if (!startDate || !endDate) return null;
    const today = moment();
    if (today.isBefore(moment(startDate), "day") || today.isAfter(moment(endDate), "day")) return null;
    return MOMENT_DAY_MAP[today.day()];
  })();

  const renderDayRows = () =>
    visibleWeekdays.length > 0 ? (
      <div className="weeklyAgendaDayList">
        {visibleWeekdays.map((day) => (
          <DayScheduleRow
            key={day}
            day={day}
            label={dayLabels[day]}
            schedule={daySchedules[day]}
            theme={theme}
            labelStartTime={labelStartTime}
            labelEndTime={labelEndTime}
            onToggle={onDayToggle}
            onStartTimeChange={onDayStartTime}
            onEndTimeChange={onDayEndTime}
            minStartTime={day === todayInRangeKey ? moment() : undefined}
          />
        ))}
      </div>
    ) : null;

  switch (typeOfVisitID) {
    case 1:
    case 2:
    case 7:
      return (
        <div className="createAgendaPermanent">
          {renderSubRetailSelect()}
          {renderPointOfSaleSelect()}

          <DatePicker
            className="createAgendaDatePicker"
            isOutsideRange={weeklyIsOutsideRange}
            clearIcon={false}
            endDate={endDate}
            onChange={(date) => handleChangeStartDate(date as DateRange)}
            orientation="horizontal"
            range
            startDatePlaceholder="mm/dd/yyyy"
            endDatePlaceholder="mm/dd/yyyy"
            format="LL"
            startDate={startDate}
            startDateLabel={labelStardDate}
            endDateLabel={labelEndDate}
            error={errorDatePicker}
          />

          {renderDayRows()}
        </div>
      );

    case 3:
      return (
        <div className="createAgendaPermanent">
          {renderSubRetailSelect(true)}
          {renderPointOfSaleSelect(true)}

          <DatePicker
            className="createAgendaDatePicker"
            isOutsideRange={weeklyIsOutsideRange}
            clearIcon={false}
            endDate={endDate}
            onChange={(date) => handleChangeStartDate(date as DateRange)}
            orientation="horizontal"
            range
            startDatePlaceholder="mm/dd/yyyy"
            endDatePlaceholder="mm/dd/yyyy"
            format="LL"
            startDate={startDate}
            startDateLabel={labelStardDate}
            endDateLabel={labelEndDate}
            error={errorDatePicker}
          />

          {renderDayRows()}
        </div>
      );

    default:
      return null;
  }
};
